<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useConfig } from 'dashboard/composables/useConfig';
import Modal from '../../../../components/Modal.vue';
import { emitter } from 'shared/helpers/mitt';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import OffersHeader from './OffersHeader.vue';
import offersAPI from '../../../../api/offers';
import offerGroupsAPI from '../../../../api/offerGroups';

const { t } = useI18n();
const router = useRouter();
const { isWasync } = useConfig();
const loading = ref(false);
const offers = ref([]);
const offerGroups = ref([]);
const isLimitReached = ref(false);

// Estado para os modais
const showDeleteModal = ref(false);
const offerToDelete = ref(null);

// Estado para edição inline
const isEditingMode = ref(false);
const offerBeingEdited = ref(null);

// Estado para criação de nova oferta
const isCreatingMode = ref(false);

// Estado para gerenciar a imagem
const selectedImage = ref(null);
const imagePreview = ref(null);

// Prop para detectar modo de criação
const props = defineProps({
  createMode: {
    type: Boolean,
    default: false,
  },
});

// Watcher para detectar quando deve iniciar o modo de criação
watch(() => props.createMode, (newValue) => {
  if (newValue) {
    startCreate();
  }
});

// Função para mapear dados da API para o formato esperado pelo componente
const mapOfferFromAPI = (offer) => ({
  id: offer.id,
  name: offer.title,
  value: offer.value,
  discount_percentage: offer.value,
  currency: offer.currency,
  type: offer.type,
  product_link: offer.product_link || '',
  created_at: offer.created_at,
  image_url: offer.image_url,
  offer_group_id: offer.offer_group_id != null ? offer.offer_group_id : '',
  offer_group_name: offer.offer_group_name || '',
  orderbump_offer_ids: Array.isArray(offer.additional_data?.orderbump_offer_ids)
    ? offer.additional_data.orderbump_offer_ids.map(Number).filter(id => Number.isInteger(id) && id > 0)
    : [],
});

// Função para mapear dados do componente para a API
const mapOfferToAPI = (offer) => ({
  title: offer.name,
  value: offer.discount_percentage,
  currency: offer.currency,
  type: offer.type,
  product_link: offer.product_link || '',
  offer_group_id:
    offer.offer_group_id === '' || offer.offer_group_id == null
      ? null
      : Number(offer.offer_group_id),
  additional_data: {
    orderbump_offer_ids: offer.orderbump_offer_ids || [],
  },
});

const fetchOfferGroups = async () => {
  try {
    const { data } = await offerGroupsAPI.list();
    offerGroups.value = Array.isArray(data) ? data : [];
  } catch {
    offerGroups.value = [];
  }
};

const refreshOfferData = async () => {
  loading.value = true;
  try {
    const response = await offersAPI.getOffers();
    offers.value = (response.data || []).map(mapOfferFromAPI);
    isLimitReached.value = !isWasync && offers.value.length >= 10;
  } catch (error) {
    console.error('Erro ao carregar ofertas:', error);
    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.MANAGER.ERROR_LOAD_DATA'),
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

const confirmDelete = offer => {
  offerToDelete.value = offer;
  showDeleteModal.value = true;
};

const handleDelete = async () => {
  try {
    loading.value = true;
    await offersAPI.deleteOffer(offerToDelete.value.id);

    // Remover da lista local
    offers.value = offers.value.filter(o => o.id !== offerToDelete.value.id);

    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.MANAGER.SUCCESS_DELETED'),
      action: { type: 'success' },
    });

    showDeleteModal.value = false;
    offerToDelete.value = null;
  } catch (error) {
    console.error('Erro ao excluir oferta:', error);
    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.MANAGER.ERROR_DELETE'),
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

const startEdit = offer => {
  offerBeingEdited.value = { ...offer };
  isEditingMode.value = true;
  isCreatingMode.value = false;
  selectedImage.value = null;
  imagePreview.value = offer.image_url || null;

  // Adicionar parâmetro 'offer' na URL
  const currentQuery = { ...router.currentRoute.value.query };
  currentQuery.offer = offer.id;
  router.replace({ query: currentQuery });
};

const startCreate = () => {
  offerBeingEdited.value = {
    name: '',
    discount_percentage: 0,
    currency: 'BRL',
    type: 'product',
    product_link: '',
    offer_group_id: '',
    offer_group_name: '',
    orderbump_offer_ids: [],
  };
  isEditingMode.value = false;
  isCreatingMode.value = true;
  selectedImage.value = null;
  imagePreview.value = null;

  // Remover parâmetro 'offer' da URL quando criando nova oferta
  const currentQuery = { ...router.currentRoute.value.query };
  delete currentQuery.offer;
  router.replace({ query: currentQuery });
};

const handleEdit = async updatedOffer => {
  try {
    loading.value = true;

    let offerData;

    // Se há uma imagem selecionada, usar FormData
    if (selectedImage.value) {
      offerData = new FormData();
      offerData.append('offer[title]', updatedOffer.name);
      offerData.append('offer[value]', updatedOffer.discount_percentage);
      offerData.append('offer[currency]', updatedOffer.currency);
      offerData.append('offer[type]', updatedOffer.type);
      offerData.append('offer[product_link]', updatedOffer.product_link || '');
      const gid = updatedOffer.offer_group_id;
      offerData.append(
        'offer[offer_group_id]',
        gid === '' || gid == null ? '' : gid
      );
      const orderbumpIds = updatedOffer.orderbump_offer_ids || [];
      orderbumpIds.forEach(orderbumpId => {
        offerData.append('offer[additional_data][orderbump_offer_ids][]', orderbumpId);
      });
      offerData.append('offer[image]', selectedImage.value);
    } else {
      offerData = mapOfferToAPI(updatedOffer);
    }

    let response;
    if (isCreatingMode.value) {
      response = await offersAPI.createOffer(offerData);
      // Adicionar na lista local (já mapeado)
      offers.value.push(mapOfferFromAPI(response.data));
    } else {
      response = await offersAPI.updateOffer(updatedOffer.id, offerData);
      // Atualizar na lista local
      const index = offers.value.findIndex(o => o.id === updatedOffer.id);
      if (index !== -1) {
        offers.value[index] = mapOfferFromAPI(response.data);
      }
    }

    const message = isCreatingMode.value ? t('KANBAN.OFFERS.MANAGER.SUCCESS_CREATED') : t('KANBAN.OFFERS.MANAGER.SUCCESS_UPDATED');
    emitter.emit('newToastMessage', {
      message,
      action: { type: 'success' },
    });

    isEditingMode.value = false;
    isCreatingMode.value = false;
    offerBeingEdited.value = null;
    selectedImage.value = null;
    imagePreview.value = null;

    // Remover parâmetro 'offer' da URL quando sair do modo de edição/criação
    const currentQuery = { ...router.currentRoute.value.query };
    delete currentQuery.offer;
    router.replace({ query: currentQuery });
  } catch (error) {
    console.error('Erro ao salvar oferta:', error);
    const message = isCreatingMode.value ? t('KANBAN.OFFERS.MANAGER.ERROR_CREATE') : t('KANBAN.OFFERS.MANAGER.ERROR_UPDATE');
    emitter.emit('newToastMessage', {
      message,
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

const duplicateOffer = async offer => {
  try {
    loading.value = true;

    const duplicatedData = mapOfferToAPI({
      ...offer,
      name: `${offer.name} (cópia)`
    });

    const response = await offersAPI.createOffer(duplicatedData);
    offers.value.push(mapOfferFromAPI(response.data));

    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.MANAGER.SUCCESS_DUPLICATED'),
      action: { type: 'success' },
    });
  } catch (error) {
    console.error('Erro ao duplicar oferta:', error);
    emitter.emit('newToastMessage', {
      message: t('KANBAN.OFFERS.MANAGER.ERROR_DUPLICATE'),
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

const handleOfferClick = offer => {
  startEdit(offer);
};

const emit = defineEmits(['switch-view', 'offer-created', 'save-offer', 'discard-changes', 'create-new-offer']);

// Handlers para o OffersHeader — voltar da edição fica na lista de ofertas, não no Kanban
const handleBack = () => {
  handleDiscardChanges();
};

const handleSaveOffer = () => {
  // Salvar a oferta atual sendo editada ou criada
  if (isEditingMode.value || isCreatingMode.value) {
    handleEdit(offerBeingEdited.value);
  }
};

const handleDiscardChanges = () => {
  isEditingMode.value = false;
  isCreatingMode.value = false;
  offerBeingEdited.value = null;
  selectedImage.value = null;
  imagePreview.value = null;

  // Remover parâmetro 'offer' da URL
  const currentQuery = { ...router.currentRoute.value.query };
  delete currentQuery.offer;
  router.replace({ query: currentQuery });
};

const handleImageSelect = (event) => {
  const file = event.target.files[0];
  if (file && file.type.startsWith('image/')) {
    selectedImage.value = file;
    
    // Criar preview da imagem
    const reader = new FileReader();
    reader.onload = (e) => {
      imagePreview.value = e.target.result;
    };
    reader.readAsDataURL(file);
  }
};

const handleCreateNewOffer = () => {
  startCreate();
};

const availableOrderbumpOffers = computed(() => {
  const currentOfferId = Number(offerBeingEdited.value?.id);
  return offers.value.filter(offer => offer.id !== currentOfferId);
});

const isOrderbumpSelected = (id) => {
  if (!offerBeingEdited.value) return false;
  return (offerBeingEdited.value.orderbump_offer_ids || []).includes(id);
};

const toggleOrderbumpOffer = (id) => {
  if (!offerBeingEdited.value) return;
  const selectedIds = offerBeingEdited.value.orderbump_offer_ids || [];
  const nextIds = selectedIds.includes(id)
    ? selectedIds.filter(item => item !== id)
    : [...selectedIds, id];

  offerBeingEdited.value = {
    ...offerBeingEdited.value,
    orderbump_offer_ids: nextIds,
  };
};

const quickValues = [25, 50, 75, 100, 150, 200, 250];
const quickValueJustClicked = ref(null);
const currencySymbols = {
  BRL: 'R$',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

const setQuickValue = (value) => {
  if (offerBeingEdited.value) {
    const current = Number(offerBeingEdited.value.discount_percentage) || 0;
    offerBeingEdited.value = { ...offerBeingEdited.value, discount_percentage: current + value };
    quickValueJustClicked.value = value;
    setTimeout(() => { quickValueJustClicked.value = null; }, 400);
  }
};

onMounted(async () => {
  await Promise.all([refreshOfferData(), fetchOfferGroups()]);

  // Verificar se há parâmetro 'offer' na URL para abrir em modo de edição
  const currentQuery = router.currentRoute.value.query;
  if (currentQuery.offer) {
    const offerId = parseInt(currentQuery.offer);
    const offer = offers.value.find(o => o.id === offerId);
    if (offer) {
      startEdit(offer);
    }
  }
});
</script>

<template>
  <div class="flex flex-col h-full bg-white dark:bg-slate-900 p-4">
    <!-- Offers Header -->
    <OffersHeader
      :editing-offer="offerBeingEdited"
      :offer-metadata="offerBeingEdited ? { updated_at: offerBeingEdited.created_at, name: offerBeingEdited.name } : {}"
      :is-creating="isCreatingMode"
      @switch-view="emit('switch-view', $event)"
      @manage-packages="emit('switch-view', 'offer-packages')"
      @back="handleBack"
      @offer-created="emit('offer-created', $event)"
      @save-offer="handleSaveOffer"
      @discard-changes="handleDiscardChanges"
      @create-new-offer="handleCreateNewOffer"
    />

    <div class="offers-content flex-1 overflow-y-auto">
      <!-- Renderiza formulário quando estiver editando ou criando -->
      <div v-if="(isEditingMode || isCreatingMode) && offerBeingEdited" class="mb-6">
        <div class="bg-white dark:bg-slate-800 rounded-lg p-6">
          <div class="mb-4">
            <h3 class="text-lg font-medium">
              {{ isCreatingMode ? t('KANBAN.OFFERS.MANAGER.CREATE_NEW_OFFER') : t('KANBAN.OFFERS.MANAGER.EDIT_OFFER') }}
            </h3>
          </div>

          <!-- Formulário: imagem à esquerda, demais campos à direita -->
          <div class="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
            <div class="w-full md:w-72 shrink-0 space-y-2">
              <label class="block text-sm font-medium">{{ t('KANBAN.OFFERS.MANAGER.IMAGE_LABEL') }}</label>
              <label
                class="flex flex-col items-center justify-center gap-2 w-full min-h-[180px] md:min-h-[220px] rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 hover:border-slate-300 dark:hover:border-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer px-3 py-4"
                :class="{ 'border-solid': imagePreview }"
              >
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
                  class="sr-only"
                  @change="handleImageSelect"
                />
                <template v-if="imagePreview">
                  <img
                    :src="imagePreview"
                    alt="Preview"
                    class="max-w-full w-full max-h-56 rounded-lg object-contain"
                  />
                  <span class="text-xs font-medium text-slate-500 dark:text-slate-400 text-center">
                    {{ t('KANBAN.OFFERS.MANAGER.CLICK_TO_CHANGE_IMAGE') }}
                  </span>
                </template>
                <template v-else>
                  <div class="flex items-center justify-center w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700">
                    <fluent-icon icon="image" size="24" class="text-slate-500 dark:text-slate-400" />
                  </div>
                  <span class="text-sm font-medium text-slate-600 dark:text-slate-300 text-center">
                    {{ t('KANBAN.OFFERS.MANAGER.CLICK_TO_CHOOSE_IMAGE') }}
                  </span>
                  <span class="text-xs text-slate-500 dark:text-slate-400 text-center">{{ t('KANBAN.OFFERS.MANAGER.IMAGE_FORMATS') }}</span>
                </template>
              </label>
            </div>

            <div class="flex-1 min-w-0 w-full space-y-4">
              <div>
                <label class="block text-sm font-medium mb-1">
                  {{ t('KANBAN.OFFERS.MANAGER.TITLE_LABEL') }}
                  <span class="text-red-600 dark:text-red-400 ml-0.5" aria-hidden="true">*</span>
                </label>
                <input
                  v-model="offerBeingEdited.name"
                  type="text"
                  class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700"
                  :placeholder="t('KANBAN.OFFERS.MANAGER.TITLE_PLACEHOLDER')"
                  required
                />
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{ t('KANBAN.OFFERS.MANAGER.PACKAGE_LABEL') }}</label>
                <select
                  v-model="offerBeingEdited.offer_group_id"
                  class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700"
                >
                  <option value="">{{ t('KANBAN.OFFERS.MANAGER.PACKAGE_NONE') }}</option>
                  <option v-for="g in offerGroups" :key="g.id" :value="g.id">
                    {{ g.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">
                  {{ t('KANBAN.OFFERS.MANAGER.TYPE_LABEL') }}
                  <span class="text-red-600 dark:text-red-400 ml-0.5" aria-hidden="true">*</span>
                </label>
                <select
                  v-model="offerBeingEdited.type"
                  class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700"
                  required
                >
                  <option value="product">{{ t('KANBAN.OFFERS.MANAGER.TYPE_PRODUCT') }}</option>
                  <option value="service">{{ t('KANBAN.OFFERS.MANAGER.TYPE_SERVICE') }}</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{ t('KANBAN.OFFERS.MANAGER.PRODUCT_LINK_LABEL') }}</label>
                <input
                  v-model="offerBeingEdited.product_link"
                  type="text"
                  autocomplete="url"
                  class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700"
                  :placeholder="t('KANBAN.OFFERS.MANAGER.PRODUCT_LINK_PLACEHOLDER')"
                />
              </div>

              <div>
                <div class="flex items-center justify-between mb-2">
                  <label class="block text-sm font-medium">Order bump (ofertas relacionadas)</label>
                  <span class="text-xs text-slate-500 dark:text-slate-400">
                    {{ (offerBeingEdited.orderbump_offer_ids || []).length }} selecionada(s)
                  </span>
                </div>
                <div
                  v-if="availableOrderbumpOffers.length"
                  class="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 p-2 bg-slate-50/50 dark:bg-slate-800/40"
                >
                  <button
                    v-for="orderbumpOffer in availableOrderbumpOffers"
                    :key="orderbumpOffer.id"
                    type="button"
                    class="w-full text-left rounded-lg border px-3 py-2 transition-colors"
                    :class="isOrderbumpSelected(orderbumpOffer.id)
                      ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'"
                    @click="toggleOrderbumpOffer(orderbumpOffer.id)"
                  >
                    <div class="flex items-start justify-between gap-2">
                      <div class="min-w-0">
                        <p class="text-sm font-medium truncate text-slate-900 dark:text-slate-100">
                          #{{ orderbumpOffer.id }} - {{ orderbumpOffer.name }}
                        </p>
                        <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {{ currencySymbols[orderbumpOffer.currency] || orderbumpOffer.currency }} {{ orderbumpOffer.value }}
                        </p>
                      </div>
                      <svg
                        v-if="isOrderbumpSelected(orderbumpOffer.id)"
                        class="w-[18px] h-[18px] text-woot-600 dark:text-woot-400 shrink-0"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.172 7.707 8.879a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <svg
                        v-else
                        class="w-[18px] h-[18px] text-slate-400 dark:text-slate-500 shrink-0"
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <circle cx="10" cy="10" r="7.25" stroke="currentColor" stroke-width="1.5" />
                      </svg>
                    </div>
                  </button>
                </div>
                <p v-else class="text-xs text-slate-500 dark:text-slate-400">
                  Crie outra oferta para poder selecionar order bumps.
                </p>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium mb-1">
                    {{ t('KANBAN.OFFERS.MANAGER.VALUE_LABEL') }}
                    <span class="text-red-600 dark:text-red-400 ml-0.5" aria-hidden="true">*</span>
                  </label>
                  <input
                    v-model.number="offerBeingEdited.discount_percentage"
                    type="number"
                    min="0.01"
                    step="0.01"
                    class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700"
                    placeholder="0.00"
                    required
                  />
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-1.5 mb-1">
                    {{ t('KANBAN.OFFERS.MANAGER.QUICK_VALUES') }}
                  </p>
                  <div class="flex flex-wrap gap-1.5">
                    <button
                      v-for="val in quickValues"
                      :key="val"
                      type="button"
                      class="group flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors"
                      :class="quickValueJustClicked === val
                        ? 'border-green-400 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600'"
                      @click="setQuickValue(val)"
                    >
                      {{ offerBeingEdited.currency === 'BRL' ? 'R$' : offerBeingEdited.currency === 'USD' ? '$' : offerBeingEdited.currency === 'EUR' ? '€' : '£' }}{{ val }}
                      <fluent-icon icon="add" size="14" class="opacity-0 group-hover:opacity-100 text-green-500 dark:text-green-400 transition-opacity" />
                    </button>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium mb-1">
                    {{ t('KANBAN.OFFERS.MANAGER.CURRENCY_LABEL') }}
                    <span class="text-red-600 dark:text-red-400 ml-0.5" aria-hidden="true">*</span>
                  </label>
                  <select
                    v-model="offerBeingEdited.currency"
                    class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700"
                    required
                  >
                    <option value="BRL">{{ t('KANBAN.OFFERS.MANAGER.CURRENCY_BRL') }}</option>
                    <option value="USD">{{ t('KANBAN.OFFERS.MANAGER.CURRENCY_USD') }}</option>
                    <option value="EUR">{{ t('KANBAN.OFFERS.MANAGER.CURRENCY_EUR') }}</option>
                    <option value="GBP">{{ t('KANBAN.OFFERS.MANAGER.CURRENCY_GBP') }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Renderiza lista de ofertas quando NÃO estiver editando -->
      <template v-else>
        <!-- Alerta de Limite -->
        <div
          v-if="isLimitReached"
          class="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
        >
          <div class="flex items-center gap-3">
            <div class="shrink-0">
              <fluent-icon
                icon="warning"
                size="20"
                class="text-amber-500 dark:text-amber-300"
              />
            </div>
            <div class="flex-1">
              <p class="text-amber-800 dark:text-amber-100 font-medium">
                {{ t('KANBAN.OFFERS.MANAGER.LIMIT_REACHED_TITLE') }}
              </p>
              <p class="text-amber-700 dark:text-amber-200 text-sm mt-1">
                {{ t('KANBAN.OFFERS.MANAGER.LIMIT_REACHED_MESSAGE') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <span class="loading-spinner" />
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!offers.length"
          class="flex flex-col items-center justify-center py-12 text-slate-600"
        >
          <fluent-icon icon="task" size="48" class="mb-4 text-slate-400" />
          <p class="text-lg">{{ t('KANBAN.OFFERS.MANAGER.EMPTY_STATE_TITLE') }}</p>
          <p class="text-sm">
            {{ t('KANBAN.OFFERS.MANAGER.EMPTY_STATE_MESSAGE') }}
          </p>
        </div>

        <!-- Offers List as Table -->
        <div v-else class="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <table class="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead class="bg-slate-50 dark:bg-slate-800/60">
              <tr>
                <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  ID
                </th>
                <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {{ t('KANBAN.OFFERS.MANAGER.IMAGE_LABEL') }}
                </th>
                <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {{ t('KANBAN.OFFERS.MANAGER.TITLE_LABEL') }}
                </th>
                <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {{ t('KANBAN.OFFERS.MANAGER.PACKAGE_COLUMN') }}
                </th>
                <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {{ t('KANBAN.OFFERS.MANAGER.TYPE_LABEL') }}
                </th>
                <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {{ t('KANBAN.OFFERS.MANAGER.PRODUCT_LINK_LABEL') }}
                </th>
                <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {{ t('KANBAN.OFFERS.MANAGER.VALUE_LABEL') }}
                </th>
                <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {{ t('KANBAN.OFFERS.MANAGER.CREATED_AT_LABEL') || 'Data' }}
                </th>
                <th scope="col" class="px-4 py-2 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {{ t('KANBAN.OFFERS.MANAGER.ACTIONS_LABEL') || 'Ações' }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-800">
              <tr
                v-for="offer in offers"
                :key="offer.id"
                class="hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                @click="handleOfferClick(offer)"
              >
                <td class="px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                  #{{ offer.id }}
                </td>
                <td class="px-4 py-3">
                  <div class="w-12 h-12 rounded-md overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    <img
                      v-if="offer.image_url"
                      :src="offer.image_url"
                      :alt="offer.name"
                      class="w-full h-full object-cover"
                    />
                    <fluent-icon
                      v-else
                      icon="image"
                      size="20"
                      class="text-slate-300 dark:text-slate-600"
                    />
                  </div>
                </td>
                <td class="px-4 py-3 text-sm text-slate-900 dark:text-white font-medium">
                  {{ offer.name }}
                </td>
                <td class="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 italic">
                  {{
                    offer.offer_group_name ||
                      t('KANBAN.OFFERS.MANAGER.PACKAGE_TABLE_EMPTY')
                  }}
                </td>
                <td class="px-4 py-3">
                  <span
                    :class="[
                      'inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full',
                      offer.type === 'service'
                        ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                    ]"
                  >
                    {{ offer.type === 'service' ? t('KANBAN.OFFERS.MANAGER.TYPE_SERVICE') : t('KANBAN.OFFERS.MANAGER.TYPE_PRODUCT') }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm">
                  <a
                    v-if="offer.product_link"
                    :href="offer.product_link"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-woot-600 dark:text-woot-400 hover:underline break-all"
                    @click.stop
                  >
                    {{ offer.product_link }}
                  </a>
                  <span v-else class="text-slate-500 dark:text-slate-400 italic">
                    -
                  </span>
                </td>
                <td class="px-4 py-3">
                  <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/20">
                    <fluent-icon icon="tag" size="16" class="text-green-600 dark:text-green-400" />
                    <span class="text-sm font-semibold text-green-700 dark:text-green-300">
                      {{ currencySymbols[offer.currency] || offer.currency }} {{ offer.value }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                  {{ new Date(offer.created_at).toLocaleDateString() }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex justify-end items-center gap-1">
                    <Button
                      variant="ghost"
                      color="slate"
                      size="sm"
                      @click.stop="startEdit(offer)"
                    >
                      <template #icon>
                        <fluent-icon icon="edit" size="16" />
                      </template>
                    </Button>
                    <Button
                      variant="ghost"
                      color="slate"
                      size="sm"
                      @click.stop="duplicateOffer(offer)"
                    >
                      <template #icon>
                        <fluent-icon icon="copy" size="16" />
                      </template>
                    </Button>
                    <Button
                      variant="ghost"
                      color="ruby"
                      size="sm"
                      @click.stop="confirmDelete(offer)"
                    >
                      <template #icon>
                        <fluent-icon icon="delete" size="16" />
                      </template>
                    </Button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <!-- Modal de Confirmação de Exclusão -->
    <Modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('KANBAN.OFFERS.MANAGER.DELETE_MODAL_TITLE') }}
        </h3>
        <p class="text-sm text-slate-600 mb-6">
          {{ t('KANBAN.OFFERS.MANAGER.DELETE_MODAL_MESSAGE') }} "{{ offerToDelete?.name }}"?
          {{ t('KANBAN.OFFERS.MANAGER.DELETE_MODAL_WARNING') }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="showDeleteModal = false"
          >
            {{ t('KANBAN.OFFERS.MANAGER.CANCEL') }}
          </Button>
          <Button
            variant="solid"
            color="ruby"
            size="sm"
            :isLoading="loading"
            @click="handleDelete"
          >
            {{ t('KANBAN.OFFERS.MANAGER.DELETE_BUTTON') }}
          </Button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border-light);
  border-top: 3px solid var(--color-woot);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.offers-content {
  min-height: 0; // Importante para o scroll funcionar
}
</style>
